import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend,
} from "recharts";
import { ArrowUp, ArrowDown, Equal } from "lucide-react";

interface CountryArmsBasic {
  country: string;
  flag: string;
  icbm: number;
  srbm_mrbm: number;
  combatDrones: number;
  loiteringMunitions: number;
  samSystems: number;
  interceptors: number;
  fighters: number;
  cruiseMissiles: number;
  productionCapacity: number;
  readiness: number;
  trend: "building" | "stable" | "depleting";
}

interface ArmsComparisonProps {
  allCountries: CountryArmsBasic[];
  countryA: string;
  countryB: string;
  onChangeA: (c: string) => void;
  onChangeB: (c: string) => void;
}

const METRICS = [
  { key: "icbm", label: "ICBMs" },
  { key: "srbm_mrbm", label: "SRBMs/MRBMs" },
  { key: "combatDrones", label: "Combat Drones" },
  { key: "loiteringMunitions", label: "Loitering Munitions" },
  { key: "samSystems", label: "SAM Systems" },
  { key: "interceptors", label: "Interceptors" },
  { key: "fighters", label: "Fighters" },
  { key: "cruiseMissiles", label: "Cruise Missiles" },
  { key: "productionCapacity", label: "Production %" },
  { key: "readiness", label: "Readiness %" },
] as const;

const DiffIndicator = ({ a, b }: { a: number; b: number }) => {
  if (a > b) return <ArrowUp className="h-3 w-3 text-green-400 inline" />;
  if (a < b) return <ArrowDown className="h-3 w-3 text-red-400 inline" />;
  return <Equal className="h-3 w-3 text-yellow-400 inline" />;
};

const ArmsComparison = ({ allCountries, countryA, countryB, onChangeA, onChangeB }: ArmsComparisonProps) => {
  const a = useMemo(() => allCountries.find((c) => c.country === countryA) ?? allCountries[0], [allCountries, countryA]);
  const b = useMemo(() => allCountries.find((c) => c.country === countryB) ?? allCountries[1], [allCountries, countryB]);

  const radarData = useMemo(() => {
    const normalize = (val: number, max: number) => Math.min(100, (val / max) * 100);
    return [
      { axis: "Ballistic", A: normalize(a.icbm + a.srbm_mrbm, 4000), B: normalize(b.icbm + b.srbm_mrbm, 4000) },
      { axis: "Drones", A: normalize(a.combatDrones + a.loiteringMunitions, 33000), B: normalize(b.combatDrones + b.loiteringMunitions, 33000) },
      { axis: "Air Defense", A: normalize(a.interceptors, 2800), B: normalize(b.interceptors, 2800) },
      { axis: "Air Attack", A: normalize(a.fighters + a.cruiseMissiles, 6700), B: normalize(b.fighters + b.cruiseMissiles, 6700) },
      { axis: "Production", A: a.productionCapacity, B: b.productionCapacity },
    ];
  }, [a, b]);

  return (
    <div className="space-y-4">
      {/* Country selectors */}
      <div className="grid grid-cols-2 gap-3">
        <Select value={countryA} onValueChange={onChangeA}>
          <SelectTrigger className="text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {allCountries.map((c) => (
              <SelectItem key={c.country} value={c.country}>{c.flag} {c.country}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={countryB} onValueChange={onChangeB}>
          <SelectTrigger className="text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {allCountries.map((c) => (
              <SelectItem key={c.country} value={c.country}>{c.flag} {c.country}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Dual Radar */}
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={radarData} outerRadius="75%">
            <PolarGrid stroke="hsl(var(--border))" />
            <PolarAngleAxis dataKey="axis" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
            <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
            <Radar name={`${a.flag} ${a.country}`} dataKey="A" stroke="hsl(200, 80%, 55%)" fill="hsl(200, 80%, 55%)" fillOpacity={0.2} strokeWidth={2} />
            <Radar name={`${b.flag} ${b.country}`} dataKey="B" stroke="hsl(0, 80%, 55%)" fill="hsl(0, 80%, 55%)" fillOpacity={0.2} strokeWidth={2} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Stat comparison table */}
      <div className="grid grid-cols-[1fr_auto_1fr] gap-x-2 gap-y-1 text-xs">
        {/* Header */}
        <div className="font-semibold text-primary text-right">{a.flag} {a.country}</div>
        <div className="font-semibold text-muted-foreground text-center">vs</div>
        <div className="font-semibold text-primary">{b.flag} {b.country}</div>

        {METRICS.map(({ key, label }) => {
          const valA = a[key] as number;
          const valB = b[key] as number;
          return (
            <div key={key} className="contents">
              <div className="text-right tabular-nums">
                <DiffIndicator a={valA} b={valB} /> {valA.toLocaleString()}
              </div>
              <div className="text-center text-muted-foreground text-[10px] self-center">{label}</div>
              <div className="tabular-nums">
                {valB.toLocaleString()} <DiffIndicator a={valB} b={valA} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Advantage summary */}
      <div className="flex gap-2 text-[10px]">
        {(() => {
          let aWins = 0, bWins = 0;
          METRICS.forEach(({ key }) => {
            const valA = a[key] as number;
            const valB = b[key] as number;
            if (valA > valB) aWins++;
            else if (valB > valA) bWins++;
          });
          return (
            <>
              <Badge className="bg-blue-400/10 text-blue-400 border-0">{a.flag} leads in {aWins}/{METRICS.length}</Badge>
              <Badge className="bg-red-400/10 text-red-400 border-0">{b.flag} leads in {bWins}/{METRICS.length}</Badge>
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default ArmsComparison;
